import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TextAnimate } from './TextAnimate';
import GlowButton from './RainbowButton';
import { Marquee } from './Marquee';
import BorderGlow from './BorderGlow';

import { GamerBackground } from './GamerBackground';

export default function GameswapHome({ isBgmi }) {
    const sectionRef = useRef(null);

    const ROTATING_TEXTS = [
        "STEAM KEYS",
        "EPIC GAMES",
        "PREMIUM ACCOUNTS",
        "GAME ASSETS",
        "GAMING GEAR"
    ];

    const [textIndex, setTextIndex] = useState(0);
    const [activeBanner, setActiveBanner] = useState(0);
    const [isMarqueePaused, setIsMarqueePaused] = useState(false);
    const b0Ref = useRef(null);
    const b1Ref = useRef(null);
    const b2Ref = useRef(null);
    const e0Ref = useRef(null);
    const e1Ref = useRef(null);
    const e2Ref = useRef(null);
    const tickRef = useRef(0);

    useEffect(() => {
        let HoverEffect;
        if (typeof window !== 'undefined') {
            HoverEffect = require('hover-effect').default || require('hover-effect');
        }

        const isPortrait = typeof window !== 'undefined' && window.innerWidth < 768;
        // Ratio is Height / Width
        const ratio = isPortrait ? (5 / 4) : (1 / 2.2);
        
        // TODO: Replace with your actual portrait banner images (e.g. '/portrait_banner1.webp')
        const banner1 = isPortrait ? '/gamer_banner1.webp' : '/gamer_banner1.webp';
        const banner2 = isPortrait ? '/gamer_banner2.webp' : '/gamer_banner2.webp';
        const banner3 = isPortrait ? '/gamer_banner3.webp' : '/gamer_banner3.webp';

        if (HoverEffect) {
            if (b0Ref.current && !e0Ref.current) {
                e0Ref.current = new HoverEffect({
                    parent: b0Ref.current,
                    intensity: 0.3,
                    image1: banner1,
                    image2: banner2,
                    displacementImage: '/myDistorsionImage.webp',
                    imagesRatio: ratio,
                    speedIn: 0.6,
                    speedOut: 0.6,
                    hover: false
                });
            }
            if (b1Ref.current && !e1Ref.current) {
                e1Ref.current = new HoverEffect({
                    parent: b1Ref.current,
                    intensity: 0.3,
                    image1: banner2,
                    image2: banner3,
                    displacementImage: '/myDistorsionImage.webp',
                    imagesRatio: ratio,
                    speedIn: 0.6,
                    speedOut: 0.6,
                    hover: false
                });
            }
            if (b2Ref.current && !e2Ref.current) {
                e2Ref.current = new HoverEffect({
                    parent: b2Ref.current,
                    intensity: 0.3,
                    image1: banner3,
                    image2: banner1,
                    displacementImage: '/myDistorsionImage.webp',
                    imagesRatio: ratio,
                    speedIn: 0.6,
                    speedOut: 0.6,
                    hover: false
                });
            }
        }
    }, []);

    const currentCycleRef = useRef(0);
    const intervalRef = useRef(null);
    const scrollTimeoutRef = useRef(null);

    useEffect(() => {
        const startBannerRotation = () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => {
                const currentCycle = currentCycleRef.current;
                const nextCycle = (currentCycle + 1) % 3;
                
                if (currentCycle === 0) e0Ref.current?.next();
                if (currentCycle === 1) e1Ref.current?.next();
                if (currentCycle === 2) e2Ref.current?.next();
                
                setActiveBanner(nextCycle);
                
                const cycleToReset = currentCycle;
                setTimeout(() => {
                    if (b0Ref.current) b0Ref.current.style.opacity = nextCycle === 0 ? '1' : '0';
                    if (b1Ref.current) b1Ref.current.style.opacity = nextCycle === 1 ? '1' : '0';
                    if (b2Ref.current) b2Ref.current.style.opacity = nextCycle === 2 ? '1' : '0';
                    
                    if (cycleToReset === 0) e0Ref.current?.previous();
                    if (cycleToReset === 1) e1Ref.current?.previous();
                    if (cycleToReset === 2) e2Ref.current?.previous();
                }, 600);
                
                currentCycleRef.current = nextCycle;
            }, 2000);
        };

        startBannerRotation();

        const handleScroll = () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            scrollTimeoutRef.current = setTimeout(() => {
                startBannerRotation();
            }, 2000);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setTextIndex(prev => (prev + 1) % ROTATING_TEXTS.length);
        }, 3500);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const handleScroll = () => setIsMarqueePaused(window.scrollY > 150);
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleMouseMove = (e) => {
        if (!sectionRef.current) return;
        const rect = sectionRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        sectionRef.current.style.setProperty('--mouse-x', `${x}px`);
        sectionRef.current.style.setProperty('--mouse-y', `${y}px`);
    };

    return (
        <section
            ref={sectionRef}
            className="relative min-h-[90vh] flex flex-col justify-center overflow-hidden bg-transparent pb-10"
            onMouseMove={handleMouseMove}
        >
            <div className="absolute inset-0 transition-opacity duration-500">
                <GamerBackground />
                {/* Neon glowing effects */}
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-[150px] pointer-events-none z-0" />
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-600/20 rounded-full blur-[150px] pointer-events-none z-0" />
            </div>

            <div className="relative z-10 mx-auto max-w-7xl px-0 md:px-[5px] lg:px-[15px] pt-[69px] lg:pt-24 w-full pointer-events-none">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="w-full mx-auto pointer-events-auto mt-0 md:mt-2"
                >
                    {/* Mobile Direct Image (Edge to Edge) */}
                    <div className="md:hidden relative w-full aspect-[4/5] overflow-hidden">
                        {/* TODO: Update these src attributes to your portrait banners (e.g., '/gamer_banner1_portrait.webp') when ready */}
                        <img src="/gamer_banner1.webp" alt="Banner 1" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${activeBanner === 0 ? 'opacity-100' : 'opacity-0'}`} />
                        <img src="/gamer_banner2.webp" alt="Banner 2" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${activeBanner === 1 ? 'opacity-100' : 'opacity-0'}`} />
                        <img src="/gamer_banner3.webp" alt="Banner 3" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${activeBanner === 2 ? 'opacity-100' : 'opacity-0'}`} />
                        
                        {/* Pagination Indicators */}
                        <div className="absolute bottom-4 right-4 flex items-center gap-2 z-20 pointer-events-none">
                            {[0, 1, 2].map((i) => (
                                <div 
                                    key={i} 
                                    className={`h-1.5 rounded-[50px] transition-all duration-500 ${
                                        activeBanner === i ? 'w-8 bg-[#f5f5f5]' : 'w-2 bg-[#f5f5f5]/40'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Desktop Card with BorderGlow */}
                    <div className="hidden md:block">
                        <BorderGlow 
                            edgeSensitivity={22} 
                            glowColor="320 100 60"
                            backgroundColor={typeof window !== 'undefined' && document.documentElement.classList.contains('dark') ? "#0a0a0a" : "#ffffff"} 
                            borderRadius={16} 
                            glowRadius={16} 
                            glowIntensity={0.4} 
                            coneSpread={28} 
                            colors={['#ec4899', '#db2777', '#be185d']} 
                            className="w-full aspect-[21/9] lg:aspect-[2.2/1] relative bg-white dark:bg-transparent rounded-2xl shadow-xl dark:shadow-none transition-colors"
                        >
                            <div className="absolute inset-0 overflow-hidden rounded-2xl w-full h-full" style={{ WebkitMaskImage: '-webkit-radial-gradient(white, black)' }}>
                                <div ref={b0Ref} className="absolute inset-0 transition-opacity duration-[0ms]" style={{ opacity: 1 }} />
                                <div ref={b1Ref} className="absolute inset-0 transition-opacity duration-[0ms]" style={{ opacity: 0 }} />
                                <div ref={b2Ref} className="absolute inset-0 transition-opacity duration-[0ms]" style={{ opacity: 0 }} />
                                
                                {/* Pagination Indicators */}
                                <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 flex items-center gap-2 z-20 pointer-events-none">
                                    {[0, 1, 2].map((i) => (
                                        <div 
                                            key={i} 
                                            className={`h-1.5 rounded-[50px] transition-all duration-500 ${
                                                activeBanner === i ? 'w-8 bg-[#f5f5f5]' : 'w-2 bg-[#f5f5f5]/40'
                                            }`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </BorderGlow>
                    </div>

                    <div className="mt-3 flex flex-row justify-between items-center px-[5px] sm:px-4 text-[9px] min-[380px]:text-[10px] md:text-xs font-semibold tracking-[0.1em] sm:tracking-[0.2em] uppercase w-full">
                        <p className="text-left shrink-0">
                            {isBgmi ? (
                                <>
                                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-500">WELCOME TO </span>
                                    <span className="font-black text-yellow-500 dark:text-yellow-400">BGMI ESPORTS</span>
                                </>
                            ) : (
                                <>
                                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-pink-600 dark:from-purple-400 dark:to-pink-500">PREVIOUSLY KNOWN AS </span>
                                    <span className="font-black text-yellow-500 dark:text-yellow-400">CHEAPSTEAM</span>
                                </>
                            )}
                        </p>
                        <p className="text-right">
                            <span className="text-gray-600 dark:text-gray-500">POWERED BY </span>
                            <span className="font-black text-yellow-500 dark:text-yellow-400">GRIDLABS PROD.</span>
                        </p>
                    </div>
                </motion.div>
            </div>

            {/* Stylish Animated Text Below Banner */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 1 }}
                className="relative z-10 mt-8 sm:mt-12 w-full overflow-hidden select-none pointer-events-auto"
            >
                    <Marquee className="[--duration:30s]" paused={isMarqueePaused}>
                        {(isBgmi ? ['ESPORTS TOURNAMENTS', 'DAILY SCRIMS', 'HUGE PRIZE POOLS'] : ['INSTANT GAME KEYS', 'UNBEATABLE PRICES', 'GLOBAL ACTIVATION']).map((text, i) => (
                            <span key={i} className="mx-4 sm:mx-8 text-4xl sm:text-6xl md:text-8xl font-black text-gray-900 dark:text-white tracking-[0.1em]" style={{ fontFamily: 'Stormfaze, sans-serif' }}>
                                {text}
                            </span>
                        ))}
                    </Marquee>
                    <Marquee reverse className="[--duration:40s] -mt-2 sm:-mt-4 lg:-mt-6" paused={isMarqueePaused}>
                        {(isBgmi ? ['SOLO SQUAD DUO', 'FAIR PLAY GUARANTEED', 'LIVE CASTING'] : ['24/7 AUTO-DELIVERY', '100% SECURE CHECKOUT', 'LIFETIME GUARANTEE']).map((text, i) => (
                            <span key={i} className="mx-4 sm:mx-8 text-3xl sm:text-5xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-500/80 via-pink-500/80 to-purple-500/80 dark:from-purple-500/20 dark:via-pink-500/20 dark:to-purple-500/20 tracking-[0.1em]" style={{ fontFamily: 'Stormfaze, sans-serif', WebkitTextStroke: '1px rgba(236,72,153,0.5)' }}>
                                {text}
                            </span>
                        ))}
                    </Marquee>
                    <div className="w-full max-w-7xl mx-auto border-b border-gray-200 dark:border-white/10 mt-12 sm:mt-16" />
            </motion.div>
        </section>
    );
}
